import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Bell, BellOff, ClipboardList, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
  useUndo,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { useMe } from '@/auth/useMe';
import { formatDateTime, WEEKDAYS } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { useEmployeeOptions, useEmployees } from '@/features/employees/api';
import {
  isOverdue,
  useTaskLists,
  useTaskMutations,
  useTaskScheduleMutations,
  useTaskSchedules,
  useTasks,
} from '../api';
import { TaskDialog } from './TaskDialog';
import { EMPTY_TASK, type TaskValues } from './taskValues';


/** ניהול משימות (PRD 5.9.2). */
export function TasksAdminPage() {
  const undo = useUndo();
  const canManage = usePerm('ops.tasks');
  const { me } = useMe();
  const tasks = useTasks();
  const lists = useTaskLists();
  const { data: options } = useEmployeeOptions();
  const { data: employees } = useEmployees();
  const mutations = useTaskMutations();
  const schedules = useTaskScheduleMutations();
  const scheduleList = useTaskSchedules();
  const [dialog, setDialog] = useState<TaskValues | null>(null);
  const [branchFilter, setBranchFilter] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  /**
   * חיפוש בכותרת ובפירוט: מי שמחפש "קרמיקה" לא זוכר אם זה היה
   * בכותרת או בהסבר.
   */
  const term = search.trim();
  const shown = (tasks.data ?? []).filter((task) => {
    if (undo.isPending(task.id)) return false;
    if (branchFilter && task.branch_id !== branchFilter) return false;
    if (term === '') return true;
    return (
      task.title.includes(term) ||
      (task.description ?? '').includes(term) ||
      `${task.employees?.first_name ?? ''} ${task.employees?.last_name ?? ''}`.includes(term)
    );
  });

  // משימה שפג תוקפה אינה פתוחה: היא ירדה מהרשימה בסוף היום.
  // היא גם אינה "הושלמה", ולכן היא מסומנת אחרת בטבלה.
  const open = shown.filter((task) => !task.completed_at && !task.expired_at);
  const done = shown.filter((task) => task.completed_at || task.expired_at);

  return (
    <AdminPage
      title="משימות"
      subtitle="מה הוטל על מי, ועד מתי"
      actions={
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/ops/tasks/templates" className="no-underline">
            <Button variant="secondary">
              <ClipboardList size={20} strokeWidth={1.75} aria-hidden="true" />
              תבניות משימה
            </Button>
          </Link>
          <Button variant="primary" onClick={() => setDialog(EMPTY_TASK)}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            משימה חדשה
          </Button>
        </div>
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}


      {/*
        סינון וחיפוש מעל הרשימות ולא בתוך כל פאנל: הם חלים על
        הפתוחות ועל שהושלמו יחד.
      */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="grid gap-1">
          <span className="text-label font-medium">חיפוש</span>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="משימה, פירוט או שם"
            className="min-h-11 w-64 max-w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink placeholder:text-muted"
          />
        </label>

        {(options?.branches ?? []).length > 1 && (
          <label className="grid gap-1">
            <span className="text-label font-medium">סניף</span>
            <select
              value={branchFilter}
              onChange={(event) => setBranchFilter(event.target.value)}
              className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
            >
              <option value="">כל הסניפים</option>
              {(options?.branches ?? []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {tasks.isPending && <Skeleton className="h-40" />}
      {tasks.isError && <ErrorState onRetry={() => void tasks.refetch()} />}

      {open.length === 0 && done.length === 0 && (
        <EmptyState
          title={branchFilter || term ? 'אין משימות שמתאימות' : 'אין משימות'}
          description={
            branchFilter || term
              ? 'אפשר לנקות את הסינון או לחפש משהו אחר.'
              : 'משימה אפשר להטיל על סניף או על עובד/ת.'
          }
        />
      )}

      {open.length > 0 && (
        <Panel title={`פתוחות (${open.length})`}>
          <TaskTable
            rows={open}
            onNotify={(id, notify) =>
              void mutations.setNotify
                .mutateAsync({ id, notify })
                .catch((caught: unknown) => setError(errorMessage(caught)))
            }
            onEdit={(task) =>
              setDialog({
                id: task.id,
                title: task.title,
                description: task.description ?? '',
                branchId: task.branch_id ?? '',
                assigneeId: task.assignee_id ?? '',
                priorityId: task.priority_id ?? '',
                // datetime-local עובד בשעון מקומי, ולכן ISO לא מתאים
                dueAt: task.due_at ? toLocalInput(task.due_at) : '',
                requiresPhoto: task.requires_photo,
                notifyOnComplete: task.notify_on_complete,
                // משימה שכבר נוצרה היא תמיד חד פעמית: החזרתיות
                // יושבת על התבנית, ולא על המופע שלה
                repeat: 'once',
                weekdays: [],
                dayOfMonth: null,
                dueTime: '',
                scheduleId: null,
              })
            }
            onRemove={(id, title) =>
              undo.remove({
                id,
                message: `"${title}" נמחקה`,
                // כשל במחיקה היה נבלע בתוך חלון החרטה
                perform: () =>
                  mutations.remove.mutateAsync(id).catch((caught: unknown) => {
                    setError(errorMessage(caught));
                    throw caught;
                  }),
              })
            }
          />
        </Panel>
      )}

      {(scheduleList.data?.length ?? 0) > 0 && (
        <Panel title={`חוזרות (${scheduleList.data?.length ?? 0})`}>
          <ul className="m-0 grid list-none gap-1 p-0">
            {(scheduleList.data ?? []).map((schedule) => (
              <li
                key={schedule.id}
                className="flex flex-wrap items-center gap-2 rounded-sm border border-line bg-cream p-2"
              >
                <span className="grid min-w-0 grow gap-0.5">
                  <span className="truncate text-body-sm font-medium text-ink">
                    {schedule.title}
                  </span>
                  <span className="truncate text-label text-muted">
                    {schedule.branches?.name ?? 'כל הסניפים'} ·{' '}
                    {schedule.employees
                      ? `${schedule.employees.first_name} ${schedule.employees.last_name}`
                      : 'כל מי שבסניף'}{' '}
                    · {repeatLabel(schedule)}
                    {schedule.due_time ? ` · עד ${schedule.due_time.slice(0, 5)}` : ''}
                  </span>
                </span>

                <Button
                  variant="ghost"
                  aria-label={`עריכת ${schedule.title}`}
                  onClick={() =>
                    setDialog({
                      id: null,
                      scheduleId: schedule.id,
                      title: schedule.title,
                      description: schedule.description ?? '',
                      branchId: schedule.branch_id ?? '',
                      assigneeId: schedule.assignee_id ?? '',
                      priorityId: schedule.priority_id ?? '',
                      dueAt: '',
                      requiresPhoto: schedule.requires_photo,
                      notifyOnComplete: schedule.notify_on_complete,
                      repeat: schedule.recurrence,
                      weekdays: schedule.weekdays ?? [],
                      dayOfMonth: schedule.day_of_month,
                      dueTime: schedule.due_time ? schedule.due_time.slice(0, 5) : '',
                    })
                  }
                >
                  <Pencil size={20} strokeWidth={1.75} aria-hidden="true" />
                </Button>
                <Button
                  variant="ghost"
                  aria-label={`מחיקת ${schedule.title}`}
                  onClick={() =>
                    void schedules.remove
                      .mutateAsync(schedule.id)
                      .catch((caught: unknown) => setError(errorMessage(caught)))
                  }
                >
                  <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
          <p className="m-0 mt-2 text-label text-muted">
            המשימה נוצרת מחדש בכל פעם שהיא חלה. מה שלא בוצע נסגר בסוף היום ולא מצטבר.
          </p>
        </Panel>
      )}

      {done.length > 0 && (
        <Panel title="נסגרו">
          <TaskTable rows={done.slice(0, 30)} />
        </Panel>
      )}
      {dialog && (
        <TaskDialog
          initial={dialog}
          branches={options?.branches ?? []}
          employees={employees ?? []}
          priorities={lists.data?.priorities ?? []}
          isSaving={mutations.create.isPending || mutations.update.isPending}
          onClose={() => setDialog(null)}
          onSave={async (values) => {
            const statusId = lists.data?.statuses.find((one) => !one.is_done_state)?.id;
            if (!statusId || !me?.employeeId) throw new Error('חסר סטטוס פתוח למשימה.');

            const shared = {
              title: values.title.trim(),
              description: values.description.trim() || null,
              branch_id: values.branchId || null,
              assignee_id: values.assigneeId || null,
              priority_id: values.priorityId || null,
              due_at: values.dueAt ? new Date(values.dueAt).toISOString() : null,
              requires_photo: values.requiresPhoto,
              notify_on_complete: values.notifyOnComplete,
            };

            if (values.repeat !== 'once') {
              await schedules.save.mutateAsync({
                id: values.scheduleId ?? null,
                title: shared.title,
                description: shared.description,
                branch_id: shared.branch_id,
                assignee_id: shared.assignee_id,
                priority_id: shared.priority_id,
                requires_photo: shared.requires_photo,
                notify_on_complete: shared.notify_on_complete,
                recurrence: values.repeat,
                weekdays: values.repeat === 'weekly' ? values.weekdays : null,
                day_of_month: values.repeat === 'monthly' ? values.dayOfMonth : null,
                due_time: values.dueTime || null,
                created_by: me.employeeId,
              });

              // משימה בודדת שהפכה לחוזרת: התבנית תיצור אותה
              // מחדש, והשורה הישנה היתה נשארת ככפילות
              if (values.id) {
                await mutations.remove.mutateAsync(values.id);
              }
              return;
            }

            if (values.id) {
              await mutations.update.mutateAsync({ id: values.id, ...shared });
            } else {
              await mutations.create.mutateAsync({
                ...shared,
                status_id: statusId,
                created_by: me.employeeId,
              });
            }
          }}
        />
      )}
    </AdminPage>
  );
}

/** "כל יום", "ראשון, רביעי", "ב 15 לחודש". */
function repeatLabel(schedule: {
  recurrence: string;
  weekdays: number[] | null;
  day_of_month: number | null;
}): string {
  if (schedule.recurrence === 'weekly') {
    const days = (schedule.weekdays ?? []).map((day) => WEEKDAYS[day]).filter(Boolean);
    return days.length > 0 ? days.join(', ') : 'בימים קבועים';
  }
  if (schedule.recurrence === 'monthly') {
    return schedule.day_of_month ? `ב ${schedule.day_of_month} לחודש` : 'פעם בחודש';
  }
  return 'כל יום';
}

/** ISO מהשרת לפורמט ש datetime-local מצפה לו, בשעון המקומי. */
function toLocalInput(iso: string): string {
  const at = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(
    at.getHours(),
  )}:${pad(at.getMinutes())}`;
}

function TaskTable({
  rows,
  onEdit,
  onNotify,
  onRemove,
}: {
  rows: ReturnType<typeof useTasks>['data'] & object;
  onEdit?: (task: (ReturnType<typeof useTasks>['data'] & object)[number]) => void;
  onNotify?: (id: string, notify: boolean) => void;
  onRemove?: (id: string, title: string) => void;
}) {
  return (
    <Table>
      <thead>
        <Tr>
          <Th>משימה</Th>
          <Th>סניף</Th>
          <Th>על מי</Th>
          <Th>עד מתי</Th>
          <Th>מצב</Th>
          {(onEdit || onNotify || onRemove) && (
            <Th>
              <span className="sr-only">פעולות</span>
            </Th>
          )}
        </Tr>
      </thead>
      <tbody>
        {rows.map((task) => (
          <Tr key={task.id}>
            <Td>
              {task.title}
              {task.requires_photo && <span className="ms-2 text-label text-muted">עם צילום</span>}
            </Td>
            <Td>{task.branches?.name ?? '—'}</Td>
            <Td>
              {task.employees
                ? `${task.employees.first_name} ${task.employees.last_name}`
                : 'הסניף'}
            </Td>
            <Td className="text-label tabular">
              {task.due_at ? formatDateTime(task.due_at) : '—'}
            </Td>
            <Td>
              {task.completed_at ? (
                <StatusBadge tone="good">הושלמה</StatusBadge>
              ) : task.expired_at ? (
                <StatusBadge tone="warn">לא בוצעה</StatusBadge>
              ) : isOverdue(task) ? (
                <StatusBadge tone="bad">באיחור</StatusBadge>
              ) : (
                <span className="text-muted">{task.task_statuses?.name}</span>
              )}
            </Td>
            {(onEdit || onNotify || onRemove) && (
              <Td className="whitespace-nowrap">
                {/*
                  מתג ההתראה על השורה ולא רק בחלון: ההחלטה אם
                  לדעת שמשהו נסגר משתנה ממשימה למשימה, ואין טעם
                  לפתוח חלון בשבילה.
                */}
                {onNotify && (
                  <Button
                    variant="ghost"
                    aria-pressed={task.notify_on_complete}
                    aria-label={
                      task.notify_on_complete
                        ? `לא להודיע לי כש"${task.title}" תבוצע`
                        : `להודיע לי כש"${task.title}" תבוצע`
                    }
                    title={
                      task.notify_on_complete
                        ? 'מקבלים התראה כשתבוצע'
                        : 'בלי התראה כשתבוצע'
                    }
                    onClick={() => onNotify(task.id, !task.notify_on_complete)}
                  >
                    {task.notify_on_complete ? (
                      <Bell size={20} strokeWidth={1.75} aria-hidden="true" className="text-ink" />
                    ) : (
                      <BellOff
                        size={20}
                        strokeWidth={1.75}
                        aria-hidden="true"
                        className="text-muted"
                      />
                    )}
                  </Button>
                )}
                {onEdit && (
                  <Button
                    variant="ghost"
                    aria-label={`עריכת ${task.title}`}
                    onClick={() => onEdit(task)}
                  >
                    <Pencil size={20} strokeWidth={1.75} aria-hidden="true" />
                  </Button>
                )}
                {onRemove && (
                  <Button
                    variant="ghost"
                    aria-label={`מחיקת ${task.title}`}
                    onClick={() => onRemove(task.id, task.title)}
                  >
                    <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                  </Button>
                )}
              </Td>
            )}
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}
